import React from 'react';
import { Invite, InviteResponseType } from 'types/invite.types';
import { ChakraAvatar } from 'app/components/Core';
import { getAvatarImage } from 'app/utils/utils';
import {
  Box,
  Button,
  Skeleton,
  SkeletonCircle,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { ListItemText, TableList } from 'app/components/ChakraTableList';
import { Link } from 'types/api.types';
import { rowHeights } from 'app/components/ChakraTableList/constants';
import { breakpoints } from 'app/chakra-theme/foundations/breakpoints';
import { ParseMessage } from '../ParseMessage';

export interface InvitesListProps {
  invites: Invite[];
  loading: boolean;
  isLoadingMoreInvites: boolean;
  respondToInvite: (
    response: InviteResponseType,
    guid: string,
    inviterName: string,
    inviterGuid: string
  ) => void;
  moreInvitesLink?: Link;
  fetchMoreInvites: (moreInvitesLink: Link) => void;
}

export const InvitesList: React.FC<InvitesListProps> = ({
  invites,
  loading,
  isLoadingMoreInvites,
  respondToInvite,
  moreInvitesLink,
  fetchMoreInvites,
}) => {
  const [isMobileView] = useMediaQuery(`(max-width: ${breakpoints.lg})`);
  return (
    <>
      {invites.length > 0 && !loading && (
        <Text mt={6} mb={4} fontSize="md" fontWeight={500}>
          Invites
        </Text>
      )}
      <TableList
        regularRowHeight={isMobileView ? rowHeights.higher : rowHeights.regular}
        loadingOptions={{
          loading,
          loadingMore: isLoadingMoreInvites,
          repeat: isLoadingMoreInvites ? 3 : 6,
          loader: {
            accessory: <SkeletonCircle width="40px" height="40px" />,
            title: <Skeleton width="350px" height="24px" />,
          },
        }}
        columns={[
          { key: 'accessory', name: '', accessory: true },
          { key: 'title', name: '' },
          { key: 'action', name: '', width: 171 },
        ]}
        rows={invites.map((invite) => {
          const {
            profileCard: {
              initials,
              profileImages,
              avatarColors,
              displayName = '',
              subject,
            },
          } = invite.inviter;
          return {
            accessory: (
              <ChakraAvatar
                size="md"
                initials={initials}
                src={getAvatarImage(profileImages)}
                colors={avatarColors}
              />
            ),
            title: (
              <ListItemText
                data-testid="notification-item-text"
                primaryTextProps={{
                  as: Box,
                  whiteSpace: 'normal',
                  wordBreak: 'break-all',
                }}
                primary={
                  <ParseMessage message={invite} isMobileView={isMobileView} />
                }
              />
            ),
            action: (
              <Box>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    respondToInvite(
                      'DECLINE',
                      invite.guid,
                      displayName,
                      subject
                    )
                  }
                >
                  Decline
                </Button>
                <Button
                  ml={2}
                  size="sm"
                  onClick={() =>
                    respondToInvite('ACCEPT', invite.guid, displayName, subject)
                  }
                >
                  Accept
                </Button>
              </Box>
            ),
          };
        })}
      />
      {moreInvitesLink?.href && (
        <Box pt={3}>
          <Button size="sm" onClick={() => fetchMoreInvites(moreInvitesLink)}>
            See more invites
          </Button>
        </Box>
      )}
    </>
  );
};
